import { ChangeEvent, FC, FocusEvent, useState } from "react"
import {
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../../../config-admin-panel"
import InputColorPicker from "../../../../../../form/input-color-picker"
import { LeadworkflowDetails } from "../../../../../../../../types"

interface Props {
  values: LeadworkflowDetails
  setConfigField: (field: string, value: any) => void
  removeConfigField: (field: string) => void
}

const BEHAVIOUR_FLAGS = [
  {
    key: "branded",
    label: "Branded",
    description: "Show brand logo and colours inside the embed"
  },
  {
    key: "validation_required",
    label: "Validation required",
    description: "Require email/phone verification before showing results"
  },
  {
    key: "phone_required",
    label: "Phone required",
    description: "Make phone number a required field"
  },
  {
    key: "subscribe_enabled",
    label: "Subscribe CTA",
    description: "Show rate alert subscription option"
  },
  {
    key: "disable_pricing",
    label: "Disable pricing",
    description: "Hide pricing results from the embed"
  },
  {
    key: "apply_now_params_enabled",
    label: "Apply now params",
    description: "Pass query params to the apply-now URL"
  },
  {
    key: "reviews_enabled",
    label: "Reviews enabled",
    description: "Display lender reviews inside the embed"
  },
  {
    key: "show_txt_msgs_disclaimer",
    label: "SMS disclaimer",
    description: "Show text message opt-in consent"
  }
] as const

const DEFAULT_KEYS = new Set(BEHAVIOUR_FLAGS.map(f => f.key))

const KNOWN_NON_BOOLEAN_KEYS = new Set([
  "steps",
  "disclaimers",
  "primary_color",
  "unique_application_url",
  "exit_cta_step",
  "background_img"
])

const LEADWORKFLOW_TEXT_INPUT_CLASS =
  "w-full max-w-xl px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"

const LEADWORKFLOW_NUMBER_INPUT_CLASS =
  "w-32 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"

const LeadworkflowBehaviour: FC<Props> = ({
  values,
  setConfigField,
  removeConfigField
}) => {
  const cfg = values.config_json
  const [adding, setAdding] = useState(false)
  const [newKey, setNewKey] = useState("")

  const setTrimmedOrRemove = (field: string, raw: string) => {
    const v = raw.trim()
    if (!v) removeConfigField(field)
    else setConfigField(field, v)
  }

  const extraFields = Object.entries(cfg).filter(
    ([k, v]) =>
      typeof v === "boolean" &&
      !DEFAULT_KEYS.has(k as any) &&
      !KNOWN_NON_BOOLEAN_KEYS.has(k)
  ) as [string, boolean][]

  const handleAdd = () => {
    const key = newKey.trim()
    if (!key) return
    setConfigField(key, false)
    setNewKey("")
    setAdding(false)
  }

  return (
    <div className="space-y-5 pt-2">
      <ConfigAdminPanel>
        {BEHAVIOUR_FLAGS.map(({ key, label, description }, i) => (
          <ConfigAdminToggleRow
            key={key}
            label={label}
            fieldKey={key}
            description={description}
            enabled={cfg[key] === true}
            onChange={val => setConfigField(key, val)}
            last={i === BEHAVIOUR_FLAGS.length - 1}
          />
        ))}
      </ConfigAdminPanel>

      <ConfigAdminPanel>
        <ConfigAdminPanelStackedField
          label="Primary color"
          fieldKey="primary_color"
          hint="Optional"
          htmlFor="primary_color"
        >
          <InputColorPicker
            showLabel={false}
            name="primary_color"
            label="Primary color"
            value={cfg.primary_color ?? ""}
            className="max-w-xl"
            inputFieldClassName={LEADWORKFLOW_TEXT_INPUT_CLASS}
            innerWrapperClassName="mt-0 relative flex rounded-md"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setTrimmedOrRemove("primary_color", e.target.value)
            }}
            onChangeWhell={(e: ChangeEvent<HTMLInputElement>) => {
              setConfigField("primary_color", e.target.value)
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              setTrimmedOrRemove("primary_color", e.target.value)
            }}
          />
        </ConfigAdminPanelStackedField>
        <ConfigAdminPanelStackedField
          label="Unique application URL"
          fieldKey="unique_application_url"
          hint="Optional. Full URL including https:// for the application link."
          htmlFor="leadworkflow-unique-application-url"
        >
          <input
            id="leadworkflow-unique-application-url"
            type="url"
            value={cfg.unique_application_url ?? ""}
            onChange={e => {
              setTrimmedOrRemove("unique_application_url", e.target.value)
            }}
            placeholder="https://example.com/apply"
            className={LEADWORKFLOW_TEXT_INPUT_CLASS}
          />
        </ConfigAdminPanelStackedField>
        <ConfigAdminPanelStackedField
          label="Exit CTA step"
          fieldKey="exit_cta_step"
          hint="Step index at which the exit call-to-action appears (0 = disabled)"
          htmlFor="leadworkflow-exit-cta-step"
          dividerBottom={false}
        >
          <input
            id="leadworkflow-exit-cta-step"
            type="number"
            min={0}
            value={cfg.exit_cta_step ?? ""}
            onChange={e => {
              const val = e.target.value
              setConfigField("exit_cta_step", val === "" ? 0 : Number(val))
            }}
            placeholder="0"
            className={LEADWORKFLOW_NUMBER_INPUT_CLASS}
          />
        </ConfigAdminPanelStackedField>
      </ConfigAdminPanel>

      <ConfigAdminPanel>
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <p className="text-xs font-medium text-gray-500">Custom fields</p>
        </div>
        {extraFields.map(([key, value], i) => (
          <ConfigAdminToggleRow
            key={key}
            label={key}
            enabled={value}
            onChange={val => setConfigField(key, val)}
            onDelete={() => removeConfigField(key)}
            last={i === extraFields.length - 1 && !adding}
          />
        ))}
        {adding && (
          <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100">
            <input
              autoFocus
              type="text"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") handleAdd()
                if (e.key === "Escape") {
                  setAdding(false)
                  setNewKey("")
                }
              }}
              placeholder="field_key"
              className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={!newKey.trim()}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false)
                setNewKey("")
              }}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
        {!adding && (
          <div className="px-4 py-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-primary-600 transition-colors"
            >
              <span className="text-base leading-none">+</span>
              Add field
            </button>
          </div>
        )}
      </ConfigAdminPanel>
    </div>
  )
}

export default LeadworkflowBehaviour
